import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import propertiesService from '../../service/api/PropertiesService';
import PropertyDialog from './PropertyDialog';
import { Property } from '../../types/Property';
import { ResidentCard } from '../../types/ResidentCard';
import { getFullAddress } from '../../utils/property';
import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import { usePagination } from '../_shared/hooks/usePagination';
import { useDebounce } from '../_shared/hooks/useDebounce';
import { ColumnDefinitions, useColumnFactory } from '../_shared/columnFactory';
import { generateFiltersFromColumns } from '../_shared/tableConfiguration';
import { SearchMode, TableFilterData } from '../../types/general-types';

export const PropertyManagement = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [properties, setProperties] = useState<Property[]>(null);

  const [propertyToDelete, setPropertyToDelete] = useState<Property>();
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Property>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);

  const cardsTemplate = (data: Property) => {
    // Sort a copy by card number so the tags always render in the same order
    // (the backend order isn't guaranteed to be stable across requests).
    const cards = [...(data.cards ?? [])].sort((a, b) => a.number.localeCompare(b.number));
    return (
      <>
        {cards.map((card: ResidentCard) => (
          <Tag
            key={card.number}
            className="mr-2"
            severity={card.downloadedAt ? 'success' : 'danger'}
            value={card.number}
          ></Tag>
        ))}
      </>
    );
  };

  const columnDefinitions: ColumnDefinitions = useMemo(
    () => ({
      city: { type: 'string', translationKey: 'properties.city' },
      street: { type: 'string', translationKey: 'properties.street' },
      houseNumber: { type: 'string', translationKey: 'properties.house_number' },
      apartmentNumber: { type: 'string', translationKey: 'properties.apartment_number' },
      zipCode: { type: 'string', translationKey: 'properties.zip_code' },
      remarks: { type: 'string', translationKey: 'properties.remarks' },
      // Filters by nested resident-card number (backend EXISTS); displays the cards
      // as tags. Not sortable.
      cards: {
        type: 'string',
        translationKey: 'properties.cards',
        sortable: false,
        bodyTemplate: cardsTemplate,
        style: { width: '40%' },
      },
    }),
    []
  );

  const DEFAULT_FILTERS = useMemo(() => ({ ...generateFiltersFromColumns(columnDefinitions) }), [columnDefinitions]);

  const {
    paginatorState,
    setPaginatorState,
    paginationParameters,
    setPaginationParameters,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination({ rows: 10, showRowsPerPageDropdown: false, orderColumn: 'city', ascending: true });

  const [filterState, setFilterState] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));
  const [filterRequest, setFilterRequest] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));

  const calculateFilterRequest = (data: TableFilterData, globalFilterValue: string) => {
    const filters = structuredClone(data);
    if (globalFilterValue) {
      filters['global'] = {
        value: globalFilterValue,
        searchMode: SearchMode.CONTAINS,
      } as any;
    }
    setFilterRequest(filters);
  };

  const debouncedCalculateFilterRequest = useDebounce(calculateFilterRequest, 400);

  const clearFilter = () => {
    setFilterState(structuredClone(DEFAULT_FILTERS));
    setFilterRequest(structuredClone(DEFAULT_FILTERS));
    setGlobalFilter('');
  };

  const loadData = async () => {
    setLoading(true);
    const response = await propertiesService.search(filterRequest, paginationParameters);

    const totalCount = response.totalCount;
    const totalPages = totalCount < 1 ? 1 : Math.ceil(totalCount / paginationParameters.page.size);
    setPaginatorState((prevState) => ({
      ...prevState,
      totalPages,
      currentPage: prevState.currentPage > totalPages ? 1 : prevState.currentPage,
    }));

    setProperties(response.data);
    setLoading(false);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // filter changes reset the page to 1, which triggers this effect anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParameters]);

  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRequest]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalFilter(value);
    debouncedCalculateFilterRequest(filterState, value);
  };

  const handleChangeFilter = (name: string, value: any, triggerDataRefresh = false) => {
    const newFilters = { ...filterState };
    if (Array.isArray(value) && value.filter((v) => v != null).length === 0) {
      value = [];
    }
    newFilters[name].value = value;
    setFilterState(newFilters);

    if (triggerDataRefresh) {
      calculateFilterRequest(newFilters, globalFilter);
    }
  };

  const handleFilterApplyClick = () => {
    calculateFilterRequest(filterState, globalFilter);
  };

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1,
        orderColumn: e.sortField,
      },
    });
  };

  const { createColumns } = useColumnFactory(filterState, handleChangeFilter, handleFilterApplyClick, t);

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const confirmDeleteProperty = (property: Property) => {
    setPropertyToDelete(property);
    setDeleteDialogVisible(true);
  };

  const deleteProperty = async () => {
    setLoading(true);
    await propertiesService.delete(propertyToDelete.id);
    setDeleteDialogVisible(false);
    await loadData();
  };

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editProperty = (property: Property) => {
    setSelectedRowToEdit(property);
    setAddEditDialogVisible(true);
  };

  const addProperty = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const viewDetails = (id: string) => {
    history.push(`/property-details/${id}`);
  };

  const propertiesTableHeader = (
    <div className="table-header">
      {t('properties.header')}
      <span>
        <Button
          type="button"
          icon="pi pi-filter-slash"
          className="p-button-outlined mr-2"
          label={t('common.clear')}
          onClick={clearFilter}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilter}
            onChange={onGlobalFilterChange}
            placeholder={t('common.search')}
          />
        </span>
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={addProperty}
        />
      </span>
    </div>
  );

  const actionTemplate = (data: Property) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => editProperty(data)}
      ></Button>
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => confirmDeleteProperty(data)}
      ></Button>
      <Button
        type="button"
        icon="pi pi-search"
        tooltip={t('properties.property_details')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        onClick={() => viewDetails(data.id)}
      ></Button>
    </span>
  );

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={hideDeleteDialog} />
      <Button
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={async () => {
          await deleteProperty();
        }}
      />
    </>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={properties}
            lazy
            paginator
            className="p-datatable-customers"
            rows={paginatorState.rows}
            dataKey="id"
            sortOrder={paginationParameters.order.isAscending ? 1 : -1}
            sortField={paginationParameters.order.orderColumn}
            onSort={handleSort}
            rowHover
            emptyMessage={t('properties.empty_message')}
            loading={loading}
            header={propertiesTableHeader}
            filterDisplay="menu"
            filters={filterState}
            paginatorTemplate={getPaginatorTemplate(paginatorState, handlePageChange, handleRowsPerPageChange)}
          >
            {createColumns(columnDefinitions)}
            <Column
              headerStyle={{ width: '12rem' }}
              style={{ minWidth: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>

          <PropertyDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            property={selectedRowToEdit}
          />

          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {propertyToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: getFullAddress(propertyToDelete) }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
